import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Stack,
  Button,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Breadcrumbs,
  Link,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InsightsIcon from "@mui/icons-material/Insights";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import LocalDrinkIcon from "@mui/icons-material/LocalDrink";
import LivestockIcon from "../icons/LivestockIcon";
import { animalApi } from "../../api/animal";
import { getMilkTrends } from "../../api/milking";
import useAuth from "../../hooks/useAuth";
import { APP_HOME_PATH } from "../../config/routes";
import { isPhase1DestinationEnabled } from "../../config/appScope";
import { format, subDays } from "date-fns";
import HerdQuickActions from "./HerdQuickActions";

function StatCard({
  title,
  value,
  subtitle,
  icon,
  onClick,
  pageLayout,
  accent = "primary",
}) {
  const accentMap = {
    primary: "primary.main",
    success: "success.main",
    warning: "warning.main",
    info: "info.main",
  };
  const bar = accentMap[accent] || accentMap.primary;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        bgcolor: "background.paper",
        border: (t) => `1px solid ${t.palette.divider}`,
        borderLeft: "5px solid",
        borderLeftColor: bar,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...(pageLayout
          ? { boxShadow: "0 4px 18px rgba(21, 101, 192, 0.08)" }
          : {}),
      }}
    >
      <CardActionArea onClick={onClick} sx={{ flex: 1, display: "flex", alignItems: "stretch" }}>
        <CardContent sx={{ py: 2.5, px: 2.25, width: "100%" }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 0.6, lineHeight: 1.2 }}
              >
                {title}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {value}
              </Typography>
              {subtitle ? (
                <Typography variant="body2" color="text.secondary" noWrap title={subtitle}>
                  {subtitle}
                </Typography>
              ) : null}
            </Stack>
            <Box
              sx={{
                borderRadius: 2,
                p: 1.25,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "action.hover",
                border: (t) => `1px solid ${t.palette.divider}`,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/** @returns {string[]} */
function accentPalette(theme) {
  return [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.secondary.main,
    "#7e57c2",
    "#00897b",
    "#78909c",
  ];
}

/**
 * Animal-focused summary: totals, categories, and primary herd actions.
 * @param {{ sectionTitle?: string, pageLayout?: boolean }} props — `pageLayout` enables hero + full-width balanced stats (animal dashboard).
 */
export default function FarmOverviewPanel({
  sectionTitle = "Animal Dashboard",
  pageLayout = false,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [fallbackAnimals, setFallbackAnimals] = useState([]);
  const [error, setError] = useState("");
  const [needsFarm, setNeedsFarm] = useState(false);

  const [milkPoints, setMilkPoints] = useState([]);
  const [milkLoading, setMilkLoading] = useState(false);
  const [milkFailed, setMilkFailed] = useState(false);

  const palette = accentPalette(theme);
  const showMilkSection = isPhase1DestinationEnabled("/milking");

  const load = async () => {
    try {
      setError("");
      setLoading(true);
      const s = await animalApi.getAnimalSummary();
      if (s && s.total != null) {
        setSummary(s);
      } else {
        const res = await animalApi.listAnimals({ page: 1, pageSize: 1000 });
        const items = res?.items || res?.data || res || [];
        setFallbackAnimals(Array.isArray(items) ? items : []);
      }
    } catch (e) {
      if (e?.response?.status === 403) {
        setNeedsFarm(true);
        setSummary(null);
        setFallbackAnimals([]);
      } else {
        try {
          const res = await animalApi.listAnimals({ page: 1, pageSize: 1000 });
          const items = res?.items || res?.data || res || [];
          setFallbackAnimals(Array.isArray(items) ? items : []);
        } catch {
          setError("Failed to load animal data.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showMilkSection) return;
    let cancelled = false;
    (async () => {
      setMilkLoading(true);
      setMilkFailed(false);
      try {
        const end = new Date();
        const start = subDays(end, 29);
        const trendsData = await getMilkTrends({
          start_date: format(start, "yyyy-MM-dd"),
          end_date: format(end, "yyyy-MM-dd"),
          aggregation: "daily",
        });
        if (!cancelled) setMilkPoints(Array.isArray(trendsData?.points) ? trendsData.points : []);
      } catch {
        if (!cancelled) {
          setMilkFailed(true);
          setMilkPoints([]);
        }
      } finally {
        if (!cancelled) setMilkLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showMilkSection]);

  const computed = useMemo(() => {
    if (summary) return summary;
    const byCategory = {};
    for (const a of fallbackAnimals) {
      const key = (a?.category || "Uncategorized").trim();
      byCategory[key] = (byCategory[key] || 0) + 1;
    }
    return { total: fallbackAnimals.length, byCategory };
  }, [summary, fallbackAnimals]);

  const categoryPairs = useMemo(() => {
    const pairs = Object.entries(computed?.byCategory || {});
    return pairs.sort((a, b) => b[1] - a[1]);
  }, [computed]);

  const milk30DayTotalLiters = useMemo(
    () =>
      milkPoints.reduce((acc, point) => acc + Number(point.total_production || 0), 0),
    [milkPoints]
  );

  const herdStats = useMemo(() => {
    const pairs = categoryPairs;
    const total = Number(computed?.total ?? 0);
    const top = pairs[0];
    const topName = top?.[0]?.trim();
    const topCount = typeof top?.[1] === "number" ? top[1] : 0;
    const topPct =
      total > 0 && topCount >= 0
        ? Math.round((topCount / total) * 100)
        : null;
    return { topName, topCount, topPct };
  }, [categoryPairs, computed?.total]);

  const showSetupGuide =
    user?.tenant_id &&
    !loading &&
    !needsFarm &&
    (computed?.total === 0 || computed?.total === "—");

  const statTiles = (
    <>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard
          title="Total animals"
          value={loading ? "…" : (computed?.total ?? "—")}
          icon={<LivestockIcon sx={{ fontSize: "1.85rem" }} />}
          accent="primary"
          pageLayout={pageLayout}
          onClick={() => navigate("/animals")}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard
          title="Largest group"
          value={
            loading
              ? "…"
              : herdStats.topName != null && herdStats.topName !== ""
                ? herdStats.topCount
                : "—"
          }
          subtitle={
            herdStats.topName
              ? String(herdStats.topName)
              : "Assign categories when adding animals"
          }
          icon={<LeaderboardIcon sx={{ fontSize: "1.85rem" }} color="warning" />}
          accent="warning"
          pageLayout={pageLayout}
          onClick={() =>
            herdStats.topName
              ? navigate(`/animals?category=${encodeURIComponent(herdStats.topName)}`)
              : navigate("/animals")
          }
        />
      </Grid>
      {showMilkSection ? (
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Milk (30 days)"
            value={
              milkLoading
                ? "…"
                : milkFailed
                  ? "—"
                  : `${Math.round(milk30DayTotalLiters).toLocaleString()} L`
            }
            subtitle="Total recorded production"
            icon={<LocalDrinkIcon sx={{ fontSize: "1.85rem" }} color="info" />}
            accent="info"
            pageLayout={pageLayout}
            onClick={() => navigate("/milking")}
          />
        </Grid>
      ) : (
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Largest share"
            value={
              loading
                ? "…"
                : herdStats.topPct != null && !Number.isNaN(herdStats.topPct)
                  ? `${herdStats.topPct}%`
                  : "—"
            }
            subtitle={
              herdStats.topName
                ? `Of herd in “${herdStats.topName}”`
                : "Needs category data"
            }
            icon={<InsightsIcon sx={{ fontSize: "1.85rem" }} />}
            accent="info"
            pageLayout={pageLayout}
            onClick={() =>
              herdStats.topName
                ? navigate(`/animals?category=${encodeURIComponent(herdStats.topName)}`)
                : navigate("/animals")
            }
          />
        </Grid>
      )}
    </>
  );

  const goCategory = (categoryName) =>
    navigate(`/animals?category=${encodeURIComponent(categoryName)}`);

  return (
    <Box sx={{ width: "100%" }}>
      {needsFarm && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {user?.roles?.includes("super_admin") ? (
            <>Platform administrator: use Admin → Tenants to manage organizations.</>
          ) : (
            <>
              Create or join a farm to use animal records.{" "}
              <Button component={RouterLink} to="/create-farm" size="small">
                Create farm
              </Button>
            </>
          )}
        </Alert>
      )}
      {showSetupGuide && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Welcome! Add your first animals and invite your team under Admin → Invites
          when you are ready.
        </Alert>
      )}
      {pageLayout && (
        <>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              component={RouterLink}
              underline="hover"
              color="inherit"
              to={APP_HOME_PATH}
            >
              Dashboard
            </Link>
            <Typography color="text.primary">Animal dashboard</Typography>
          </Breadcrumbs>
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: { xs: 2.5, md: 3.5 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              boxShadow: 1,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "flex-start" }}
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ minWidth: 0, flex: { md: "0 1 360px" } }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "success.main",
                    color: "success.contrastText",
                    boxShadow: 1,
                  }}
                >
                  <LivestockIcon sx={{ fontSize: { xs: 28, sm: 30 } }} />
                </Box>
                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: -0.5,
                      color: "text.primary",
                      lineHeight: 1.2,
                      fontSize: { xs: "1.35rem", md: "2.125rem" },
                    }}
                  >
                    {sectionTitle}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.75, maxWidth: 520 }}
                  >
                    Headline counts and categories for your herd. Jump to the
                    full list or add animals in a few clicks.
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ width: "100%", minWidth: 0, flex: { md: "1 1 420px" } }}>
                <HerdQuickActions onRefresh={load} showRefresh buttonSize="medium" />
              </Box>
            </Stack>
          </Paper>
        </>
      )}
      {!pageLayout && (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5, gap: 1 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {sectionTitle}
            </Typography>
            <Tooltip title="Refresh">
              <IconButton
                onClick={load}
                aria-label="Refresh herd summary"
                size="medium"
                sx={{
                  bgcolor: "background.paper",
                  border: (t) => `1px solid ${t.palette.divider}`,
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <HerdQuickActions onRefresh={load} showRefresh={false} buttonSize="medium" />
          <Divider sx={{ mb: 3, mt: 2 }} />
        </>
      )}

      {pageLayout && <Divider sx={{ mb: 3, opacity: 0.85 }} />}

      <Stack spacing={pageLayout ? 2.5 : 3} sx={{ width: "100%", mb: 1 }}>
        <Grid
          container
          spacing={pageLayout ? 2.5 : 2}
          alignItems="stretch"
          sx={{ width: "100%", m: 0 }}
        >
          {statTiles}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            width: "100%",
            alignSelf: "stretch",
            boxSizing: "border-box",
            ...(pageLayout ? { boxShadow: "0 4px 18px rgba(21, 101, 192, 0.06)" } : {}),
          }}
        >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  color: pageLayout ? "success.dark" : "text.primary",
                  letterSpacing: pageLayout ? 0.15 : undefined,
                }}
              >
                By category
              </Typography>
              <InsightsIcon fontSize="small" color={pageLayout ? "success" : "action"} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Open a category to jump to Animals with that filter applied.
            </Typography>

            {loading ? (
              <Stack direction="row" alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : categoryPairs.length === 0 ? (
              <Typography color="text.secondary">
                No categories found yet. Assign categories as you add animals.
              </Typography>
            ) : (
              <Grid container spacing={pageLayout ? 2 : 1.5}>
                {categoryPairs.map(([cat, count], index) => (
                  <Grid item xs={12} sm={6} md={4} lg={4} key={cat}>
                    <Card
                      variant="outlined"
                      elevation={0}
                      sx={{
                        borderRadius: 2.5,
                        height: "100%",
                        border: (t) => `1px solid ${t.palette.divider}`,
                        borderLeftWidth: 5,
                        borderLeftStyle: "solid",
                        borderLeftColor: palette[index % palette.length],
                        bgcolor: "background.paper",
                        ...(pageLayout
                          ? {
                              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
                            }
                          : {}),
                        ":hover": {
                          boxShadow: 2,
                          transform: "translateY(-2px)",
                        },
                        transition: "transform 0.18s ease, box-shadow 0.18s ease",
                      }}
                    >
                      <CardActionArea
                        onClick={() => goCategory(cat)}
                        sx={{ height: "100%", alignItems: "stretch" }}
                        aria-label={`View ${cat} animals`}
                      >
                        <CardContent sx={{ py: 2.5 }}>
                          <Typography variant="overline" color="text.secondary">
                            {cat}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            {count}
                          </Typography>
                          <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: "block" }}>
                            View animals →
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
        </Paper>
      </Stack>
    </Box>
  );
}
